<script setup>
import dayjs from 'dayjs'
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { stringToColor, textColor } from '@/lib/utils'

import { usePlanningStore } from '@/stores/planning.store'
import { useFilteredOptions } from '@/composables/select-filter'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useScheduleStore } from '@/stores/schedule.store'

import { useNotify } from '@/composables/notify'
import { useProjectDeleteOptions } from '@/composables/delete-options'
import { useStepper } from '@/composables/stepper'
import { useIsUpdated, useClone } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useTagDraft } from '@/composables/tag'
import { pickExchangeRate, referenceCurrencyHint, useExchangeRateEdit } from '@/composables/exchange-rate'
import { useCompanyResolve, useContactResolve, useContactReferences, useListingCompany } from '@/composables/company-resolve'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import ListingSelect from '@/components/shared/ListingSelect.vue'
import ServiceMultiSelect from '@/components/shared/ServiceMultiSelect.vue'
import CurrencySelect from '@/components/shared/CurrencySelect.vue'
import CompanyCombobox from '@/components/shared/CompanyCombobox.vue'
import ContactSelect from '@/components/shared/ContactSelect.vue'
import ContactReferenceInput from '@/components/shared/ContactReferenceInput.vue'
import DateTimeRangeStep from '@/components/shared/DateTimeRangeStep.vue'
import DeleteOptionsDialog from '@/components/shared/DeleteOptionsDialog.vue'
import ProjectRescheduleDialogs from '@/components/shared/ProjectRescheduleDialogs.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import CreateModeToggle from '@/components/shared/CreateModeToggle.vue'
import ExchangeRatePanel from '@/components/shared/ExchangeRatePanel.vue'
import TagCreateDialog from '@/components/shared/TagCreateDialog.vue'
import ColorInput from '@/components/shared/ColorInput.vue'

const i18n = getI18n()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const scheduleStore = useScheduleStore()
const { notifySuccess, notifyError } = useNotify()
const { isMobile } = useDetectDevice()

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  changes: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  initialStep: {
    type: Number,
    required: false,
    default: null,
  },
  initialQuickCreate: {
    type: Boolean,
    required: false,
    default: false,
  },
  initialServiceIds: {
    type: Array,
    required: false,
    default: () => [],
  },
  initialContactReferences: {
    type: Object,
    required: false,
    default: null,
  },
})

const { step, nextStep, prevStep, markAttempted, wasAttempted } = useStepper({
  step: props.initialStep ?? planningStore.projectEditStepEnum.BASIC_INFO,
})

const form = ref(null)
const quickCreate = ref(props.initialQuickCreate && !props.modelValue.id)
const quickStep = ref(1)
const editItem = ref(props.modelValue.id ? useClone(props.modelValue) : {
  start: dayjs(`${dayjs().format('YYYY-MM-DD')}T${webSiteStore.getDefaultStartTime()}`).toISOString(),
  end: dayjs(`${dayjs().format('YYYY-MM-DD')}T${webSiteStore.getDefaultEndTime()}`).toISOString(),
  color: '#027be3',
  tags: [],
  currency_ref: webSiteStore.getDefaultCurrency().id,
  ...useClone(props.modelValue),
})
const schedules = ref(!props.modelValue.id && props.modelValue.schedules?.length ? useClone(props.modelValue.schedules) : [
  { start_time: webSiteStore.getDefaultStartTime(), end_time: webSiteStore.getDefaultEndTime() },
])
const originalSchedules = ref([])
const originalStart = ref(null)
const originalEnd = ref(null)
const projectServiceIds = ref([...props.initialServiceIds])
const originalServiceIds = ref([])
const listingServiceIds = ref(null)

const { isUpdated: isItemUpdated, isUpdating, original, setOriginal } = useIsUpdated(editItem)
const { onCurrencyChange, onExchangeRateChange, confirmRepriceRowsAsync } = useExchangeRateEdit(editItem, original)
const { resolveCompany, rollbackCompany } = useCompanyResolve()
const { resolveContact, notifyContactResolved } = useContactResolve()
const { contactReferences, contactReferencesChanged, loadContactReferences } = useContactReferences()
if (props.initialContactReferences) loadContactReferences({ ...contactReferences.value, ...props.initialContactReferences })
const { companyReadonly, setCompany, applyListingCompany } = useListingCompany(editItem)

const servicesChanged = computed(() => {
  if (projectServiceIds.value.length !== originalServiceIds.value.length) return true
  const current = new Set(projectServiceIds.value)
  return originalServiceIds.value.some(id => !current.has(id))
})

const schedulesChanged = computed(() => {
  if (schedules.value.length !== originalSchedules.value.length) return true
  return schedules.value.some((s, i) => {
    const o = originalSchedules.value[i]
    return !o || s.start_time !== o.start_time || s.end_time !== o.end_time
  })
})

const isUpdated = computed(() => isItemUpdated.value || servicesChanged.value || schedulesChanged.value || contactReferencesChanged.value)

const loadListingServiceIds = async (listingId) => {
  if (!listingId) { listingServiceIds.value = null; return }
  const items = await planningStore.fetchListingItemsAsync({ listing: listingId, all: true })
  listingServiceIds.value = new Set((items.data || items || []).map(i => i.service))
}

onMounted(async () => {
  const fetches = [planningStore.fetchProjectTagsAsync({ all: true }, true)]
  if (planningStore.equipments.length === 0) {
    fetches.push(planningStore.fetchEquipmentsAsync({ all: true }, true))
  }
  if (planningStore.staff.length === 0) {
    fetches.push(planningStore.fetchStaffsAsync({ all: true }, true))
  }
  await Promise.all(fetches)

  if (props.modelValue.id) {
    const [projectRes, byServiceRes] = await Promise.all([
      planningStore.fetchProjectByIdAsync(props.modelValue.id),
      scheduleStore.fetchEventsByServiceForProjectAsync(props.modelValue.id),
    ])
    editItem.value = { ...projectRes.data }
    editItem.value.tags = editItem.value.tag_ids || []
    if (editItem.value.schedules && editItem.value.schedules.length > 0) {
      schedules.value = editItem.value.schedules.map(s => ({
        id: s.id,
        start_time: s.start_time?.substring(0, 5) || s.start_time,
        end_time: s.end_time?.substring(0, 5) || s.end_time,
      }))
    }
    originalSchedules.value = schedules.value.map(s => ({ ...s }))
    originalStart.value = editItem.value.start
    originalEnd.value = editItem.value.end
    projectServiceIds.value = (byServiceRes.data || []).map(e => e.service_id)
    originalServiceIds.value = [...projectServiceIds.value]
    if (editItem.value.listing) {
      await loadListingServiceIds(editItem.value.listing)
    }
    applyListingCompany()
    setOriginal(editItem.value)
    Object.assign(editItem.value, props.changes)
  } else {
    editItem.value.listing ??= planningStore.listings.find(l => l.is_generic)?.id
    if (editItem.value.listing) {
      await loadListingServiceIds(editItem.value.listing)
      applyListingCompany()
    }
  }
})

const emit = defineEmits(['update', 'cancel', 'delete'])
useNavigator(() => {
  emit('cancel')
})
useEscapeClose(isUpdated, () => emit('cancel'))

const requiredRule = (val) => (val !== null && val !== undefined && val !== '') || i18n.t('common.required')

const basicInfoHasError = computed(() => !editItem.value.name || editItem.value.listing == null || editItem.value.currency_ref == null)

const { options: availableServices, onFilter: onServiceFilter } = useFilteredOptions(() => {
  const assigned = new Set(projectServiceIds.value)
  const services = planningStore.getServices().filter(s => !assigned.has(s.id))
  return listingServiceIds.value ? services.filter(s => listingServiceIds.value.has(s.id)) : services
})

const onListingChange = async (val) => {
  editItem.value.listing = val
  applyListingCompany()
  projectServiceIds.value = []
  await loadListingServiceIds(val)
}

const datesChanged = computed(() =>
  !dayjs(editItem.value.start).isSame(originalStart.value) || !dayjs(editItem.value.end).isSame(originalEnd.value),
)

const rescheduleDialogs = ref(null)

// A date/slot change reflows the existing service allocations: warn (dates) or offer a
// choice (slots only), unless the project is already in progress (block), then save.
const onUpdate = async (itemId) => {
  const repriceRows = await confirmRepriceRowsAsync()
  if (repriceRows === null) return
  const decision = await rescheduleDialogs.value.confirm({
    project: editItem.value,
    hasAllocations: originalServiceIds.value.length > 0,
    datesChanged: datesChanged.value,
    slotsChanged: schedulesChanged.value,
  })
  if (decision.blocked || decision.cancelled) return
  await performSave(itemId, decision.action, repriceRows)
}

const performSave = async (itemId, allocationAction, repriceRows = false) => {
  isUpdating.value = true
  const typedCompany = editItem.value.company
  const typedContact = editItem.value.contact
  let createdCompanyId = null
  let resolvedContact = null
  let projectSaved = false
  try {
    const { id: companyId, createdId } = await resolveCompany(typedCompany)
    createdCompanyId = createdId
    editItem.value.company = companyId
    resolvedContact = await resolveContact(companyId, typedContact, contactReferences.value)
    editItem.value.contact = resolvedContact.id
    const payload = { ...editItem.value, reprice_rows: repriceRows }
    delete payload.budget_document
    delete payload.tag_ids
    delete payload.listing_items
    payload.schedules = schedules.value
    if (allocationAction !== null) payload.allocation_action = allocationAction
    const result = await planningStore.upsertProjectAsync(payload)
    projectSaved = true
    const projectId = itemId || result.data.id
    const currentSet = new Set(projectServiceIds.value)
    const originalSet = new Set(originalServiceIds.value)
    const toAdd = projectServiceIds.value.filter(id => !originalSet.has(id))
    const toRemove = originalServiceIds.value.filter(id => !currentSet.has(id))
    const rangeOps = [
      ...toAdd.map(serviceId =>
        scheduleStore.syncRangeAsync(serviceId, projectId, editItem.value.start, editItem.value.end, {
          quantity: 1,
          listingId: editItem.value.listing,
        }),
      ),
    ]
    if (toRemove.length) {
      const { scheduleApi } = await import('@/apis/schedule.api')
      const eventsRes = await scheduleApi.getEventsAsync({ project_ids: String(projectId) })
      const events = eventsRes?.data?.results ?? eventsRes?.data ?? eventsRes ?? []
      for (const serviceId of toRemove) {
        const event = events.find(ev => ev.service_id === serviceId && ev.budget_document_row_id)
        if (event) {
          rangeOps.push(scheduleStore.deleteRangeAsync(event.budget_document_row_id))
        }
      }
    }
    if (rangeOps.length) await Promise.all(rangeOps)
    notifySuccess(itemId ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    notifyContactResolved(resolvedContact.result)
    editItem.value.id = editItem.value.id || result.data.id
    emit('update', editItem.value)
  } catch (error) {
    if (!projectSaved) {
      await resolvedContact?.rollback()
      await rollbackCompany(createdCompanyId)
      editItem.value.company = typedCompany
      editItem.value.contact = typedContact
    }
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
  } finally {
    isUpdating.value = false
  }
}

const onCancel = () => emit('cancel')

const { deleteOptions, openDeleteOptions, onDeleteOptionSelect } = useProjectDeleteOptions({
  onDeleted: () => emit('delete'),
})

const showDeleteDialog = ref(false)

const onConfirmDelete = () => { showDeleteDialog.value = true }

const onDeleteConfirmed = async () => {
  try {
    await planningStore.deleteProjectAsync(editItem.value.id, { skipErrorMessage: true })
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    emit('delete')
  } catch (error) {
    if (error.response?.status === 409) {
      openDeleteOptions(editItem.value, error)
    } else {
      notifyError(i18n.t('error.deleteFailed'))
    }
  }
}

const projectTagFilter = ref('')

const onProjectTagFilter = (val, update) => {
  projectTagFilter.value = val.toLowerCase()
  update()
}

const availableProjectTags = computed(() => {
  const items = planningStore.getLocalizedProjectTags()
  if (!projectTagFilter.value) return items
  return items.filter(t => t.name?.toLowerCase().includes(projectTagFilter.value))
})

const { tagDraft, openTagDraft, confirmTagDraftAsync } = useTagDraft(planningStore.createProjectTagAsync)
const onCreateProjectTag = (val, done) => {
  if (!val) return
  const existing = planningStore.getLocalizedProjectTags().find(t => t.name?.toLowerCase() === val.toLowerCase())
  if (existing) { done(existing.id); return }
  openTagDraft(val, done)
}

const onDownloadAttachment = async (file) => {
  try {
    const response = await fetch(file.url)
    const blob = await response.blob()

    const blobUrl = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = blobUrl
    link.download = file.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Errore nel download del file:', error)
    window.open(file.url, '_blank')
  }
}

</script>
<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <tag-create-dialog
      :draft="tagDraft"
      :kinds="planningStore.getLocalizedProjectTagKinds()"
      @confirm="confirmTagDraftAsync"
      @cancel="tagDraft = null"
    />
    <navigate-back
      :text="editItem.id ? i18n.t('common.editProject') : i18n.t('common.createProject')"
      @cancel="onCancel"
    >
      <template v-if="!editItem.id" #right>
        <create-mode-toggle v-model="quickCreate" />
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'dialog-card-content': editItem.id }">
      <q-space />
      <q-card-section class="q-px-sm">
        <q-form ref="form">
          <q-stepper
            v-if="quickCreate"
            v-model="quickStep"
            class="q-py-none"
          >
            <q-step
              :name="1"
              :title="i18n.t('common.quickCreate')"
              icon="mdi-flash"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.name')"
                  autofocus
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <ListingSelect
                  :model-value="editItem.listing"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :rules="[requiredRule]"
                  @update:model-value="onListingChange"
                />
                <currency-select
                  :model-value="editItem.currency_ref"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :clearable="false"
                  :rules="[requiredRule]"
                  :hint="referenceCurrencyHint(editItem.currency_ref)"
                  @update:model-value="onCurrencyChange"
                />
                <exchange-rate-panel
                  :model-value="pickExchangeRate(editItem)"
                  :currency-ref="editItem.currency_ref"
                  class="col-12"
                  @update:model-value="onExchangeRateChange"
                />
                <form-section-title :label="i18n.t('common.customer')" />
                <company-combobox
                  :model-value="editItem.company"
                  :readonly="companyReadonly"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @update:model-value="setCompany"
                />
                <contact-select
                  v-model="editItem.contact"
                  :company-id="editItem.company"
                  :disable="!editItem.company"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @load-references="loadContactReferences"
                />
                <contact-reference-input
                  v-model="contactReferences[planningStore.referenceTypeEnum.PHONE]"
                  :contact="editItem.contact"
                  :reference-type="planningStore.referenceTypeEnum.PHONE"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <contact-reference-input
                  v-model="contactReferences[planningStore.referenceTypeEnum.EMAIL]"
                  :contact="editItem.contact"
                  :reference-type="planningStore.referenceTypeEnum.EMAIL"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <form-section-title :label="i18n.t('common.period')" />
                <div class="col-12">
                  <date-time-range-step
                    v-model="editItem"
                    :schedules="schedules"
                    @update:schedules="schedules = $event"
                  />
                </div>
              </div>
              <stepper-navigation
                :finish="() => form.validate().then(success => success && onUpdate(editItem.id))"
                :finish-label="i18n.t('common.createProject')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
          <q-stepper
            v-else
            v-model="step"
            class="q-py-none"
          >
            <q-step
              :name="planningStore.projectEditStepEnum.BASIC_INFO"
              :title="i18n.t('common.basicInfo')"
              icon="mdi-briefcase-plus-outline"
              :done="!!editItem.id || step > planningStore.projectEditStepEnum.BASIC_INFO"
              :header-nav="!!editItem.id || step > planningStore.projectEditStepEnum.BASIC_INFO"
              :error="wasAttempted(planningStore.projectEditStepEnum.BASIC_INFO) && basicInfoHasError"
              error-color="negative"
              error-icon="mdi-alert-circle"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.name')"
                  autofocus
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <ListingSelect
                  :model-value="editItem.listing"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :rules="[requiredRule]"
                  @update:model-value="onListingChange"
                />
                <currency-select
                  :model-value="editItem.currency_ref"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :clearable="false"
                  :rules="[requiredRule]"
                  :readonly="!!(editItem.id && editItem.has_cost_report_rows)"
                  :hint="referenceCurrencyHint(editItem.currency_ref)"
                  @update:model-value="onCurrencyChange"
                />
                <q-select
                  v-model="editItem.status"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.status')"
                  option-value="id"
                  option-label="name"
                  :options="planningStore.getLocalizedProjectStatuses()"
                />
                <exchange-rate-panel
                  :model-value="pickExchangeRate(editItem)"
                  :currency-ref="editItem.currency_ref"
                  class="col-12"
                  @update:model-value="onExchangeRateChange"
                />
                <form-section-title :label="i18n.t('common.customer')" />
                <company-combobox
                  :model-value="editItem.company"
                  :readonly="companyReadonly"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @update:model-value="setCompany"
                />
                <contact-select
                  v-model="editItem.contact"
                  :company-id="editItem.company"
                  :disable="!editItem.company"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @load-references="loadContactReferences"
                />
                <contact-reference-input
                  v-model="contactReferences[planningStore.referenceTypeEnum.PHONE]"
                  :contact="editItem.contact"
                  :reference-type="planningStore.referenceTypeEnum.PHONE"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <contact-reference-input
                  v-model="contactReferences[planningStore.referenceTypeEnum.EMAIL]"
                  :contact="editItem.contact"
                  :reference-type="planningStore.referenceTypeEnum.EMAIL"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <form-section-title :label="i18n.t('common.details')" />
                <q-input
                  v-model="editItem.description"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.description')"
                  autogrow
                />
                <color-input v-model="editItem.color" :class="isMobile ? 'col-12' : 'col-3'" />
                <q-select
                  v-model="editItem.tags"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.tag', 2)"
                  option-value="id"
                  option-label="name"
                  :options="availableProjectTags"
                  multiple
                  use-chips
                  use-input
                  emit-value
                  map-options
                  new-value-mode="add-unique"
                  :menu-offset="[20, 8]"
                  @filter="onProjectTagFilter"
                  @new-value="onCreateProjectTag"
                >
                  <template #selected-item="chip">
                    <q-chip
                      v-if="chip.opt"
                      removable
                      size="sm"
                      :tabindex="chip.tabindex"
                      class="q-ma-none"
                      :color="chip.color ? chip.color : 'initial'"
                      :style="{ 'background-color': stringToColor(chip.opt.name), 'color': textColor(stringToColor(chip.opt.name)), 'margin': 0, 'margin-right': '.25rem' }"
                      @remove="chip.removeAtIndex(chip.index)"
                    >
                      {{ chip.opt.name }}
                    </q-chip>
                  </template>
                </q-select>
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(planningStore.projectEditStepEnum.BASIC_INFO); form.validate().then((success) => (success ? nextStep() : null)) }"
              />
            </q-step>

            <q-step
              :name="planningStore.projectEditStepEnum.DATE_TIME"
              :title="i18n.t('project.edit.addDateAndTime')"
              icon="mdi-calendar-clock"
              :done="!!editItem.id || step > planningStore.projectEditStepEnum.DATE_TIME"
              :header-nav="!!editItem.id || step > planningStore.projectEditStepEnum.DATE_TIME"
            >
              <date-time-range-step
                v-model="editItem"
                :schedules="schedules"
                :continue-action="nextStep"
                :back-action="prevStep"
                @update:schedules="schedules = $event"
              />
            </q-step>

            <q-step
              :name="planningStore.projectEditStepEnum.SERVICES"
              :title="i18n.t('project.edit.addServices')"
              icon="mdi-filmstrip"
              :done="!!editItem.id || step > planningStore.projectEditStepEnum.SERVICES"
              :header-nav="!!editItem.id || step > planningStore.projectEditStepEnum.SERVICES"
            >
              <service-multi-select
                :model-value="projectServiceIds.map(id => ({ id, quantity: 1 }))"
                :label="i18n.t('common.service', 2)"
                :options="availableServices"
                @update:model-value="projectServiceIds = $event.map(c => c.id)"
                @filter="onServiceFilter"
              />
              <stepper-navigation :continue="nextStep" :back="prevStep" />
            </q-step>

            <q-step
              :name="planningStore.projectEditStepEnum.ATTACHMENTS"
              :title="i18n.t('common.additionalInfo', 2)"
              :caption="i18n.t('common.optional')"
              icon="mdi-attachment"
              :done="!!editItem.id"
              :header-nav="!!editItem.id"
            >
              <file-url-edit
                bordered
                caption
                :model-value="editItem.project_files"
                :urls="editItem.project_urls"
                class="q-mt-sm"
                :mobile="isMobile"
                @update:model-value="editItem.project_files = $event"
                @update:urls="editItem.project_urls = $event"
                @download="onDownloadAttachment"
              />

              <stepper-navigation
                :back="prevStep"
                :finish="editItem.id ? null : () => onUpdate(editItem.id)"
                :finish-label="i18n.t('common.createProject')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
      <q-space />
    </q-card-section>

    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="onConfirmDelete()"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="onUpdate(editItem.id)"
      />
    </q-card-actions>
    <delete-options-dialog
      v-model="deleteOptions.show"
      :title="deleteOptions.title"
      :message="deleteOptions.message"
      :caption="deleteOptions.caption"
      :options="deleteOptions.options"
      @select="onDeleteOptionSelect"
    />
    <project-reschedule-dialogs ref="rescheduleDialogs" />
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmed"
    />
  </q-card>
</template>
